import { SortDirection } from "@bridgesplit/utils";
import {
    ParsedTransactionInstruction,
    BaseRouteStep,
    RouteStepParams,
    WindRouteStep,
    UnwindRouteStep
} from "@loopscale/pandora";

import { AssetData } from "./metadata";
import { ExpectedLoanValues } from "./arguments";

export interface LoopVaultInfoExpanded extends LoopVaultInfo {
    maturityDate?: Date;
    protocols?: Protocol[];
    assetTagType?: LoopAssetTypeTag[];
    metadataTags?: LoopMetadataTag[];
    targetRedemptionMint?: string;
}

export interface LoopVaultInfo {
    collateralMint: string;
    principalMint: string;
    collateralDeposited: number;
    collateralApyPct: number;
    maxLeverage: number;
    name: string;
    maxLeveragedApyPct: number;
    metadata?: MeteoraLoopMetadata | null;
    principalAmountAvailable: number;
    routeType?: LoopRouteType | null;
    tags?: LoopFilterTags[];
    collateralDepositedUsd: number;
    quoteFetchParams: BaseRouteStepExpanded; //TODO: use pandora upgraded package
    maxCollateralAmount?: number;
    aumLimit?: number;
    feVisible: boolean;
    wAvgLeverage?: number;
    wAvgApy?: number;
    oneWeekCollateralDepositsUsd?: number;
    creationTime?: number;
    featured?: boolean;
    rolloverId?: string;
}

// temporary type until backend is updated, then will use type exported from pandora
export type ExtendedUnwindRouteStep = UnwindRouteStep & {
    collateralDecimals: number;
    principalDecimals: number;
    maturedMarket?: boolean;
    maturedPtMint?: string;
    version?: string;
    maturedVersion?: string;
};

export enum SwapType {
    Jup = "jup",
    Meteora = "meteora",
    Perena = "perena",
    NoSwap = "noSwap"
}

export interface OrcaRouteParams {
    type: "orca";
    whirlpool: string;
    luts?: string[];
    tickUpper: number;
    tickLower: number;
    tokenAMint: string;
    tokenBMint: string;
    swapType: SwapType;
    primarySwapParams?: {
        dexes?: string[];
        onlyDirectRoutes?: boolean;
    };
    positionParams: {
        existingPosition?: boolean;
        positionPrincipalInput?: number;
        principalMint: string;
    };
}

export type RouteStepParamsExpanded = RouteStepParams | OrcaRouteParams;

export type BaseRouteStepExpanded = QuoteFetchParams & RouteStepParamsExpanded;

export type WindRouteStepExpanded = WindRouteStep & RouteStepParamsExpanded;

// This will create a union type that preserves the discriminated union structure
// but removes the specified properties from each variant
export type QuoteFetchParams = {
    [K in BaseRouteStep["type"]]: Omit<Extract<BaseRouteStep, { type: K }>, "inputMint" | "outputMint"> & {
        targetRedemptionMint?: string;
    };
}[BaseRouteStep["type"]];

export interface JupQuoteFetchParams {
    onlyDirectRoutes?: boolean;
    maxAccounts?: number;
    dexes?: string[];
}

export interface MeteoraLoopMetadata {
    aTokenVault: string;
    bTokenVault: string;
    lpMint: string;
    lutAddress: string;
    lutAddresses: string;
    poolAddress: string;
    tokenAMint: string;
    tokenBMint: string;
    vaultA: string;
    vaultALp: string;
    vaultALpMint: string;
    vaultB: string;
    vaultBLp: string;
    vaultBLpMint: string;
}
/**
 * Loop tag enums for categorizing and filtering loop vaults. They are the JSON tags column returned from `loop_vaults` table
 *
 * Protocol: Used for filtering loops by their underlying protocol (e.g., Meteora, Exponent)
 * LoopFilterTags: Used for filtering loops by their characteristics (e.g., SOL-pegged, DEX LP)
 * LoopAssetTypeTag: Used to determine asset types for loops, particularly for old chart functionality (e.g., LST, LRT)
 */

export enum Protocol {
    Meteora = "Meteora",
    Exponent = "Exponent",
    RateX = "RateX",
    Sanctum = "Sanctum",
    FlashTrade = "Flash.Trade",
    Fragmetric = "Fragmetric",
    Adrastea = "Adrastea",
    Stabble = "Stabble",
    Solayer = "Solayer"
}

export enum LoopFilterTags {
    Leverage = "Leverage",
    SolPegged = "SOL-Pegged",
    StablecoinPegged = "Stablecoin-Pegged",
    DexLp = "DEX LP",
    FixedYield = "Fixed Yield",
    Farming = "Farming",
    Rwa = "RWA",
    Directional = "Directional",
    CLMM = "Leveraged Market Making"
}

export enum LoopAssetTypeTag {
    LST = "LST",
    LRT = "LRT"
}

export enum LoopMetadataTag {
    New = "New",
    Leverage = "Leverage"
}

export type LoopTag = LoopFilterTags | Protocol | LoopAssetTypeTag | LoopMetadataTag;

export enum LoopRouteType {
    Jup,
    Meteora,
    Stabble,
    Exponent,
    Flash,
    Adrena,
    Ratex,
    Orca
}

export interface LoopInfoParams {
    loopVaults?: string[];
    showHidden: boolean;
}

export interface UserLoopFilter {
    principalMint?: string;
    collateralMint?: string;
    active: boolean;
    sortDirection?: SortDirection;
    sortType?: LoopPositionSortType;
    page: number;
    pageSize: number;
}

export enum LoopPositionSortType {
    StartTime,
    LeverageMultiplier,
    NetApy,
    NetPositionValue,
    HealthRatio
}

export interface UserLoopInfoParams extends UserLoopFilter {
    loopVaults?: string[];
    loanVaults?: string[];
}

export interface LoopYieldParams {
    collateral: string[];
}

export interface IncreaseLeverageTransactionInput {
    principalRequested: BorrowPrincipalRequested[];
    depositCollateral: DepositCollateralInput[];
    cpiIxs?: ParsedTransactionInstruction[];
    cpiLuts?: string[];
    cpiSigners?: string[];
    loanAddress?: string;
    setupIxs?: ParsedTransactionInstruction[];
    setupSigners?: string[];
}

export interface BorrowPrincipalRequested {
    ledgerIndex: number;
    principalAmount: number;
    principalMint: string;
    strategy: string;
    durationIndex: number;
    expectedLoanValues: ExpectedLoanValues;
}

export interface DepositCollateralInput {
    collateralAmount: number;
    collateralAssetData: AssetData;
    loanCreationParams?: LoanCreationParams;
    weightMatrixUpdate?: [number, number, number, number, number]; // TODO: Define proper type for ParsedMatrixUpdate
}

export enum LoopWindType {
    Collateral = 0,
    Principal = 1
}

export interface OrcaPositionCreateParams {
    upperTick: number;
    lowerTick: number;
    tickSpacing: number;
    whirlpool: string;
    tokenMintA: string;
    tokenMintB: string;
}

export type LoanCreationParams = "Default" | { OrcaPositionCreate: OrcaPositionCreateParams };

export interface OrcaCreateLoanParams {
    loanCreationParams: LoanCreationParams;
}

export interface UserLoopInfoExpanded extends UserLoopInfo {
    netApyLive?: number;
}

export interface WithdrawalInfo {
    withdrawnTokenMint: string;
    withdrawnTokenAmount: number;
}

export interface UserLoopInfo {
    loanAddress: string;
    leverageMultiplier: number; // TODO: FULLY DEPRECATE
    initialCollateralAmount: number;
    loopVaultIdentifier: string;
    netApy?: number; // cached server net apy. price and time values can be stale
    positionStartTime: number;
    healthRatio?: number;
    netPositionValue?: number;
    index: number;
    totalCount: number;
    collateralYieldPct: number;
    principalYieldPct: number;
    withdrawalInfo?: WithdrawalInfo; // amount withdrawn on loop close
    depositMint?: string;
    loopVaultDeposits: LoopVaultDeposit[];
    loopVaultWithdrawals: LoopVaultWithdrawal[];
}

export interface LoopVaultDeposit {
    id: number;
    depositAmount: number;
    leverageMultiplier: number;
    loanAddress?: string;
    timestamp: number;
    depositMint?: string;
    txnSignature?: string;
    usdPrice?: number;
}

export interface LoopVaultWithdrawal {
    id: number;
    amount: number;
    loanAddress: string;
    leverageMultiplier?: number;
    assetIdentifier: string;
    timestamp: number;
    txnSignature?: string;
    usdPrice?: number;
}
